import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Fetcher } from '@this/src/util';
import { useAITTheme } from '@this/shared/ui/theme';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import OrganizationAnnouncementModal from '@this/components/admin/organization_announcements/organization_announcement_modal';
import type { OrganizationAnnouncementArg } from '@this/domain/organization_announcement/organization_announcement';
import OrganizationAnnouncement from '@this/domain/organization_announcement/organization_announcement';
import { getStyles } from './organization_announcements.styles';

const MAX_ANNOUNCEMENTS = 3;

interface Props {}

interface Response {
  organization_announcements: OrganizationAnnouncementArg[];
}

const OrganizationAnnouncements: React.FC<Props> = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isLoading, setIsLoading] = useState(false);
  const [organizationAnnouncements, setOrganizationAnnouncements] = useState<OrganizationAnnouncement[]>([]);
  const [organizationAnnouncement, setOrganizationAnnouncement] = useState<OrganizationAnnouncement | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  const fetchOrganizationAnnouncements = useCallback(async () => {
    setIsLoading(true);
    Fetcher.get<Response>('/admin/organization_announcements.json')
      .then(response => {
        setOrganizationAnnouncements(
          response.organization_announcements.map(raw => new OrganizationAnnouncement(raw))
        );
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchOrganizationAnnouncements();
  }, [fetchOrganizationAnnouncements]);

  const openAdd = useCallback(() => {
    setOrganizationAnnouncement(null);
    setModalType('add');
    setShowModal(true);
  }, []);

  const openEdit = useCallback((data: OrganizationAnnouncement) => {
    setOrganizationAnnouncement(data);
    setModalType('mod');
    setShowModal(true);
  }, []);

  const openDelete = useCallback((data: OrganizationAnnouncement) => {
    setOrganizationAnnouncement(data);
    setModalType('delete');
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const atMaxAnnouncements = organizationAnnouncements.length >= MAX_ANNOUNCEMENTS;

  return (
    <div style={styles.pageLayout}>
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle}>管理者向けのお知らせ</h2>
        <Button
          type="button"
          color="primary"
          style={styles.addButton}
          disabled={isLoading || atMaxAnnouncements}
          onClick={openAdd}
        >
          お知らせを追加
        </Button>
      </div>
      <div style={styles.contentIn}>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="e2e-table-organization-announcements" style={styles.tableWrapper}>
            {organizationAnnouncements.length === 0 ? (
              <div style={styles.emptyMessage}>管理者向けのお知らせはありません</div>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={styles.tableHeaderCell}>1行目</TableCell>
                    <TableCell style={styles.tableHeaderCell}>2行目</TableCell>
                    <TableCell style={styles.tableHeaderCell}>URL</TableCell>
                    <TableCell style={styles.tableHeaderCellActions}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizationAnnouncements.map(data => (
                    <TableRow hover key={data.id} data-e2e-id={data.id}>
                      <TableCell className="e2e-title1" style={styles.tableBodyCellText}>
                        {data.title1}
                      </TableCell>
                      <TableCell className="e2e-title2" style={styles.tableBodyCellText}>
                        {data.title2}
                      </TableCell>
                      <TableCell className="e2e-url" style={styles.tableBodyCellUrl}>
                        {data.url}
                      </TableCell>
                      <TableCell style={styles.tableBodyCellActions}>
                        <div style={styles.rowActions}>
                          <Button
                            type="button"
                            color="primary"
                            size="small"
                            style={styles.tableActionButton}
                            disabled={isLoading}
                            onClick={() => openEdit(data)}
                          >
                            編集
                          </Button>
                          <Button
                            type="button"
                            color="danger"
                            size="small"
                            style={styles.tableActionButton}
                            disabled={isLoading}
                            onClick={() => openDelete(data)}
                          >
                            削除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>
      {showModal ? (
        <OrganizationAnnouncementModal
          open
          onClose={closeModal}
          afterSubmit={fetchOrganizationAnnouncements}
          id={organizationAnnouncement?.id}
          organizationAnnouncement={organizationAnnouncement}
          modalType={modalType}
        />
      ) : null}
    </div>
  );
};

export default OrganizationAnnouncements;
